import styled from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'
import { Typography } from '@healingpaper/cell'
import { useTranslation } from '@/lang'

import Text from '@/components/atomic/atoms/Text'
import Section from '@/components/atomic/molecules/Section'
import ReviewCard from '@/components/atomic/organisms/ReviewCard/new'
import ReviewLoginCard from '@/components/atomic/organisms/ReviewLoginCard'

import type { Review } from '@/features/review/api/review.type'

const MAX_VISIBLE_REVIEW_COUNT = 3

export interface ReviewTemplateProps {
  /** 리뷰 데이터 목록 */
  reviews: Review[]
  /** 전체 리뷰 개수 (더보기 버튼 노출 판단 및 타이틀 표시용) */
  totalCount: number

  /** 섹션 타이틀 (ReactNode) */
  title?: React.ReactNode

  /** 잠김 상태 여부 (true일 경우 로그인 유도 카드 노출) */
  isLocked?: boolean

  /** 더보기 링크 정보 */
  moreLink?: {
    href: string
    onClick?: () => void
    rel?: 'nofollow' | 'follow'
  }

  /** 로그인 버튼 클릭 핸들러 (잠김 상태일 때 사용) */
  onLoginClick?: () => void

  /** 리뷰 카드 클릭 핸들러 */
  onReviewClick?: () => void

  /** 추가 스타일링을 위한 className */
  className?: string
}

const ReviewTemplate = ({
  reviews,
  totalCount,
  title,
  isLocked = false,
  moreLink,
  onLoginClick,
  onReviewClick,
  className,
}: ReviewTemplateProps): JSX.Element => {
  const { t } = useTranslation()

  // 잠김 상태 (비로그인 사용자 등)
  if (isLocked) {
    return (
      <Section hasDivider={false} padding={0} className={className}>
        {title && (
          <StyledHeading element="h2" type="title3">
            {title}
          </StyledHeading>
        )}
        <StyledDummyReviewWrapper>
          <ReviewLoginCard
            text={{
              title: t('review.loginCard.title'),
              doLogin: t('review.loginCard.doLogin'),
            }}
            onClickLoginButton={onLoginClick}
          />
          <StyledWarning type="description2">{t('review.warning')}</StyledWarning>
        </StyledDummyReviewWrapper>
      </Section>
    )
  }

  const isReviewVisible = reviews && reviews.length > 0
  const isMoreReviewButtonVisible = moreLink && isReviewVisible && totalCount > MAX_VISIBLE_REVIEW_COUNT

  return (
    <Section hasDivider={false} padding={0} className={className}>
      {title && (
        <StyledHeading element="h2" type="title3">
          {title}
        </StyledHeading>
      )}

      {isReviewVisible && (
        <StyledReviewWrapper>
          {reviews.slice(0, 3).map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              text={t('review.ReviewCard', { returnObjects: true })}
              onClickReviewCard={onReviewClick}
            />
          ))}
        </StyledReviewWrapper>
      )}

      {isMoreReviewButtonVisible && (
        <div className="pt-4">
          <Link href={moreLink.href} onClick={moreLink.onClick} rel={moreLink.rel || 'follow'}>
            <div
              className="flex flex-row items-center justify-center py-2 px-4 w-full h-[36px] bg-[#ECECEC] rounded-[8px]"
              aria-label="more review button"
            >
              <Typography type="title4" elementType="span">
                {t('review.seeMore')}
              </Typography>
              <IcArrowRight width={16} height={16} />
            </div>
          </Link>
        </div>
      )}
    </Section>
  )
}

const StyledHeading = styled(Text)`
  margin-bottom: 16px;
`
const StyledDummyReviewWrapper = styled.div`
  padding-bottom: unset !important;
`

const StyledWarning = styled(Text)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledReviewWrapper = styled.div`
  margin-top: 16px;

  > div:last-child {
    div[role='divider'] {
      display: none;
    }
  }
`

export default ReviewTemplate
